import React, { useContext, useMemo } from 'react';
import EnhancedChart from './MultiChart';
import { Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';

// Interfaces for wait time data
export interface IWaitTimeDistribution {
  withinWeek: number;
  withinTwoWeeks: number;
  withinMonth: number;
  overMonth: number;
}

export interface IAppointmentTypeWaitTimeStats {
  appointmentType: string;
  departmentUuid: string;
  departmentName: string;
  totalAppointments: number;
  avgWaitTime: number;
  minWaitTime: number;
  maxWaitTime: number;
  medianWaitTime: number;
  percentile90WaitTime: number;
  waitTimeDistribution: IWaitTimeDistribution;
  waitTimeDistributionPercentage: IWaitTimeDistribution;
}

export interface IAppointmentWaitTimeStats {
  status: number;
  waitTimeStats?: IAppointmentTypeWaitTimeStats[];
  errorMessage?: string;
}

interface AppointmentWaitTimeDistributionChartProps {
  data: IAppointmentWaitTimeStats;
  title?: string;
  chartType?: 'bar' | 'pie' | 'donut';
  horizontal?: boolean;
  usePercentage?: boolean;
}

// The four buckets read as a severity ramp, not as four unrelated categories: a wait
// inside a week is the good outcome and a wait past a month is the one a service has to
// act on. Left to the palette they came out in four arbitrary hues that all looked alike,
// so the chart said nothing at a glance. Colours are named here and travel with the
// series -- see the explicit-colour branch in MultiChart.
const WAIT_TIME_BUCKETS = [
  { id: 'hospital.analytics.wait_time.bucket.within_week', color: '#43A047' },      // Green
  { id: 'hospital.analytics.wait_time.bucket.within_two_weeks', color: '#1E88E5' }, // Blue
  { id: 'hospital.analytics.wait_time.bucket.within_month', color: '#FFB300' },     // Amber
  { id: 'hospital.analytics.wait_time.bucket.over_month', color: '#E53935' },       // Red
] as const;

const AppointmentWaitTimeDistributionChart = ({
  data,
  title = "Wait Time Distribution by Department",
  chartType = 'bar',
  horizontal = true,
  usePercentage = true
}: AppointmentWaitTimeDistributionChartProps) => {
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  
  // Transform data for the chart
  const chartData = useMemo(() => {
    if (!data.waitTimeStats || data.waitTimeStats.length === 0) {
      return {
        series: [],
        departments: [],
        categories: []
      };
    }
    
    const bucketLabels = WAIT_TIME_BUCKETS.map((bucket) => translate(bucket.id) as string);
    const departments = data.waitTimeStats && Array.isArray(data.waitTimeStats)
      ? [...new Set(data.waitTimeStats.map(item => item.departmentName))].sort()
      : [];

    const departmentDistributions = departments.map((dept) => {
      const deptItems = data.waitTimeStats?.filter(item => item.departmentName === dept) || [];
      const total = deptItems.reduce((sum, item) => sum + item.totalAppointments, 0);

      const counts = deptItems.reduce(
        (agg, item) => ({
          withinWeek: agg.withinWeek + (item.waitTimeDistribution?.withinWeek || 0),
          withinTwoWeeks: agg.withinTwoWeeks + (item.waitTimeDistribution?.withinTwoWeeks || 0),
          withinMonth: agg.withinMonth + (item.waitTimeDistribution?.withinMonth || 0),
          overMonth: agg.overMonth + (item.waitTimeDistribution?.overMonth || 0),
        }),
        { withinWeek: 0, withinTwoWeeks: 0, withinMonth: 0, overMonth: 0 },
      );

      if (!usePercentage || total === 0) {
        return {
          withinWeek: counts.withinWeek,
          withinTwoWeeks: counts.withinTwoWeeks,
          withinMonth: counts.withinMonth,
          overMonth: counts.overMonth,
        };
      }

      return {
        withinWeek: Number(((counts.withinWeek / total) * 100).toFixed(1)),
        withinTwoWeeks: Number(((counts.withinTwoWeeks / total) * 100).toFixed(1)),
        withinMonth: Number(((counts.withinMonth / total) * 100).toFixed(1)),
        overMonth: Number(((counts.overMonth / total) * 100).toFixed(1)),
      };
    });

    const series = bucketLabels.map((label, bucketIndex) => ({
      name: label,
      color: WAIT_TIME_BUCKETS[bucketIndex].color,
      data: departmentDistributions.map((distribution) => {
        const values = [
          distribution.withinWeek,
          distribution.withinTwoWeeks,
          distribution.withinMonth,
          distribution.overMonth,
        ];
        return values[bucketIndex];
      }),
    }));

    return {
      series,
      departments,
      categories: departments,
    };
  }, [data, usePercentage, translate]);
  
  // For pie/donut charts, we need to transform the data
  const pieChartData = useMemo(() => {
    if (!['pie', 'donut'].includes(chartType) || !data?.waitTimeStats || !Array.isArray(data.waitTimeStats) || data.waitTimeStats.length === 0) {
      return null;
    }
    
    // For pie charts, we'll look at the overall distribution across all departments
    const distributionField = usePercentage ? 'waitTimeDistributionPercentage' : 'waitTimeDistribution';
    
    const totalAppointments = data.waitTimeStats.reduce((sum, item) => sum + (item?.totalAppointments || 0), 0);
    
    // Calculate overall distribution
    const overallDistribution = {
      withinWeek: 0,
      withinTwoWeeks: 0,
      withinMonth: 0,
      overMonth: 0
    };
    
    data.waitTimeStats.forEach(item => {
      const weight = item.totalAppointments / totalAppointments;
      const dist = item[distributionField];
      
      overallDistribution.withinWeek += usePercentage ? dist.withinWeek * weight : dist.withinWeek;
      overallDistribution.withinTwoWeeks += usePercentage ? dist.withinTwoWeeks * weight : dist.withinTwoWeeks;
      overallDistribution.withinMonth += usePercentage ? dist.withinMonth * weight : dist.withinMonth;
      overallDistribution.overMonth += usePercentage ? dist.overMonth * weight : dist.overMonth;
    });
    
    // If not using percentages, we need to normalize the data for the pie chart
    if (!usePercentage) {
      const total = Object.values(overallDistribution).reduce((a, b) => a + b, 0);
      Object.keys(overallDistribution).forEach(key => {
        overallDistribution[key as keyof IWaitTimeDistribution] = 
          Math.round(overallDistribution[key as keyof IWaitTimeDistribution] / total * 100);
      });
    }
    
    const series = [
      {
        name: 'Within 1 Week',
        data: [Math.round(overallDistribution.withinWeek)]
      },
      {
        name: 'Within 2 Weeks',
        data: [Math.round(overallDistribution.withinTwoWeeks)]
      },
      {
        name: 'Within 1 Month',
        data: [Math.round(overallDistribution.withinMonth)]
      },
      {
        name: 'Over 1 Month',
        data: [Math.round(overallDistribution.overMonth)]
      }
    ];
    
    return {
      series,
      categories: ['All Departments'],
      total: 100
    };
  }, [data, chartType, usePercentage]);
  
  // Format title component
  const titleComponent = (
    <Typography variant="h6">
      {title} {usePercentage ? '(%)' : ''}
    </Typography>
  );
  
  // Choose the right data based on chart type
  const chartProps = ['pie', 'donut'].includes(chartType) && pieChartData
    ? {
        series: pieChartData.series,
        title: titleComponent,
        categories: pieChartData.categories,
        type: chartType,
        total: pieChartData.total
      }
    : {
        series: chartData.series,
        title: titleComponent,
        categories: chartData.categories,
        type: 'bar' as 'bar' | 'line' | 'area' | 'radar' | 'scatter' | 'heatmap' | 'pie' | 'donut',
        stacked: true,
        horizontal,
        
      };

  return <EnhancedChart {...chartProps} />;
};

export default AppointmentWaitTimeDistributionChart;